/**
 * PORTED — RO-3-00 / #44398.
 * Origin: `excellent/app/database/objects/_index-client.tsx`.
 *
 * Changes, and only these:
 *   `"use client"`                          -> dropped (inert in this host)
 *   `useRouter`      from next/navigation   -> `useNavigate` from @excellent/ui/router
 *   `useSearchParams` from next/navigation  -> `useSearch`   from @excellent/ui/router
 *
 * `useSearch()` and not TanStack's typed `useSearch` from the route: this is a
 * SHARED component with no route of its own, so it cannot name a route's search
 * schema. `?q=` is a shell-wide key (`client/shell/search.ts` reserves it — the
 * dock owns the query, the view mirrors it, CLAUDE.md rule 7), so reading it
 * untyped through the seam is correct here and does not steal a name.
 *
 * ⚠️ `rows` still arrives as a PROP. In the Next tree the server page read the
 * object-kind inventory out of the vault and passed it down; there is no
 * `objects.list` Operation, so nothing in this host can fetch it yet. Whoever
 * ports `/database/objects` (RO-3-11) owes that endpoint — see
 * `docs/rollover/inventory/shared-render.md` §5. Until then this component is
 * fully functional from props (which is how the three `*ScopeActions` callers use
 * it) and cannot self-populate.
 */

import { useMemo } from "react";
import { useNavigate, useSearch } from "@excellent/ui/router";
import {
  Button,
  DataTable,
  IntentBadge,
  PageHeader,
  ScopedSearchButton,
  formatDate,
  type DataTableColumn,
  type Intent,
} from "@excellent/ui";
import { Plus } from "@excellent/ui/icons";

export interface ObjectKindRow {
  kind: string;
  label: string;
  /** "commitment" | "party" for data-defined kinds; "built-in" for registry kinds. */
  storage: "commitment" | "party" | "built-in";
  /** null for built-in kinds (no per-kind record store to count). */
  records: number | null;
  fields: number;
  origin: "system" | "user" | "agent";
  /** ISO date; null for built-in kinds. */
  created: string | null;
  /** Detail route for data-defined kinds; null for read-only built-ins. */
  href: string | null;
}

const ORIGIN_INTENT: Record<ObjectKindRow["origin"], Intent> = {
  system: "neutral",
  user: "info",
  agent: "success",
};

const STORAGE_LABEL: Record<ObjectKindRow["storage"], string> = {
  commitment: "Commitment",
  party: "Party",
  "built-in": "Built-in",
};

export interface ObjectsIndexClientProps {
  rows: ObjectKindRow[];
  /**
   * Optional entity-scope filter (Issue 20). When set, the inventory shows ONLY
   * the object kinds in this list — the raw-data cut for a single entity-backed
   * page (Finance → its money-account/transaction kinds), reusing this one
   * `DataTable`. Additive: omitting it keeps the global `/database/objects`
   * behavior (every kind), matched case-insensitively against a row's `kind`.
   */
  scopeKinds?: string[];
  /** Hide the page-chrome (title + New-object action) when embedded in a Sheet /
   * overlay whose header already names the scope. Default false. */
  chromeless?: boolean;
}

/**
 * The searchable Objects inventory (PRD §3.4). The dock owns the query, scoped
 * to this page (Issue 19 / SCOPED-SEARCH-PRD): the header carries a magnifying-
 * glass button that focuses the scoped root search, and the page mirrors the
 * query from the URL `?q=`, filtering the table's rows. A click on a data-defined
 * kind routes to its detail page; built-in kinds are non-interactive.
 *
 * `scopeKinds` (Issue 20) narrows the inventory to one entity's kinds so the same
 * table can render in-page as an entity's "raw data" view without a detour to
 * `/database/*`.
 */
export function ObjectsIndexClient({
  rows,
  scopeKinds,
  chromeless = false,
}: ObjectsIndexClientProps) {
  const navigate = useNavigate();
  const searchParams = useSearch();
  const query = searchParams.get("q") ?? "";

  const scoped = useMemo(() => {
    if (!scopeKinds || scopeKinds.length === 0) return rows;
    const allow = new Set(scopeKinds.map((k) => k.toLowerCase()));
    return rows.filter((r) => allow.has(r.kind.toLowerCase()));
  }, [rows, scopeKinds]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return scoped;
    return scoped.filter(
      (r) => r.label.toLowerCase().includes(q) || r.kind.toLowerCase().includes(q),
    );
  }, [scoped, query]);

  const columns = useMemo<DataTableColumn<ObjectKindRow>[]>(
    () => [
      {
        key: "label",
        header: "Label",
        render: (r) => <span className="font-medium text-foreground">{r.label}</span>,
      },
      {
        key: "kind",
        header: "Kind",
        render: (r) => <span className="font-mono type-subtext text-muted-foreground">{r.kind}</span>,
      },
      {
        key: "storage",
        header: "Storage",
        render: (r) => <span className="text-muted-foreground">{STORAGE_LABEL[r.storage]}</span>,
      },
      {
        key: "records",
        header: "Records",
        render: (r) => (
          <span className="tabular-nums text-muted-foreground">
            {r.records === null ? "—" : r.records}
          </span>
        ),
      },
      {
        key: "fields",
        header: "Fields",
        render: (r) => <span className="tabular-nums text-muted-foreground">{r.fields}</span>,
      },
      {
        key: "origin",
        header: "Origin",
        render: (r) => (
          <IntentBadge intent={ORIGIN_INTENT[r.origin]}>
            {r.origin === "system" ? "System" : r.origin === "user" ? "User" : "Agent"}
          </IntentBadge>
        ),
      },
      {
        key: "created",
        header: "Created",
        render: (r) => (
          <span className="text-muted-foreground">
            {r.created ? formatDate(r.created) : "—"}
          </span>
        ),
      },
    ],
    [],
  );

  const table = (
    <DataTable
      rows={filtered}
      columns={columns}
      rowKey={(r) => r.kind}
      strongHeader
      onRowClick={(r) => {
        if (r.href) navigate.push(r.href);
      }}
      empty={
        query.trim()
          ? `No objects match “${query.trim()}”.`
          : scopeKinds && scopeKinds.length > 0
            ? "No raw rows for this entity yet."
            : "No objects yet."
      }
    />
  );

  // Chromeless: embedded in an overlay/Sheet whose header already names the
  // scope (Issue 20). Render just the table, no page chrome or outer padding.
  if (chromeless) return table;

  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-8 px-6 py-8">
        <PageHeader
          title="Objects"
          description="Every entity kind — built-in and data-defined — in one inventory. Design your own nouns without a migration."
          toolbar={
            <div className="flex items-center gap-2">
              <ScopedSearchButton label="Search objects" />
              <Button asChild size="icon-tb">
                <a
                  href="/database/objects/new"
                  aria-label="New object type"
                  title="New object type"
                >
                  <Plus strokeWidth={1.5} className="size-4" />
                </a>
              </Button>
            </div>
          }
        />
        {table}
      </div>
    </div>
  );
}
